import React from "react"
import {
  SvgDashboard,
  SvgLearn,
  SvgVideoOutline,
  SvgChat,
  SvgListCheck,
  SvgUsers,
  SvgArrowRight,
  SvgVerified,
} from "../../../components/icons"
import { useNavigate } from "react-router-dom"

interface Props {
  pathname: string
  jotformRef?: React.RefObject<HTMLDivElement>
  isBootcamp?: boolean
  isCountdownActive?: boolean
}

const ProgramFeatures: React.FC<Props> = ({
  pathname,
  jotformRef,
  isBootcamp,
  isCountdownActive,
}) => {
  const navigate = useNavigate()

  const handleBootcampRegistration = () => {
    navigate("/5daybusinessbootcamp/registration")
  }

  const scrollToJotform = () => {
    if (jotformRef?.current) {
      jotformRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  const features = isBootcamp
    ? [
        {
          title: "5 Live Training Sessions",
          description:
            "Daily 90-minute expert-led interactive workshops cover how to master each tier of Zoee’s Hierarchy of Needs for Coaches™, from essential building blocks to high-leverage business strategy.",
          icon: SvgVideoOutline,
        },
        {
          title: "A Comprehensive Workbook",
          description:
            "Your step-by-step companion with practical exercises and resources to turn ideas into action, reference key lessons, and keep you focused and moving forward.",
          icon: SvgLearn,
        },
        {
          title: "Daily Challenges",
          description:
            "High-impact, manageable action steps that generate momentum and results in real time for tangible business outcomes in just one week.",
          icon: SvgListCheck,
        },
        {
          title: "Peer Community",
          description:
            "Connect with other coaches, ask questions, share insights, and build relationships that expand your network inside the exclusive training community.",
          icon: SvgUsers,
        },
        {
          title: "Personalized Roadmap",
          description:
            "By the end of 5 days, you’ll have a clear, custom plan for building and scaling your coaching business so you know exactly how to move forward.",
          icon: SvgDashboard,
        },
        {
          title: "NBHWC Accredited",
          description:
            "Recognized by the National Board for Health & Wellness Coaching (NBHWC), this program qualifies for 7.5 Continuing Coach Education (CCE) credits, supporting your ongoing professional development.",
          icon: SvgVerified,
        },
      ]
    : [
        {
          title: "All-in-one Online Business",
          description:
            "Three-month access to the Zoee platform with guidance on how to set up an easy-to-manage, streamlined, and more profitable practice",
          icon: SvgDashboard,
        },
        {
          title: "A Comprehensive Workbook",
          description:
            "Weekly workbook chapters with essential exercises and targeted tips, such as how to use AI in your business, provide a practical step-by-step business development strategy",
          icon: SvgLearn,
        },
        {
          title: "3 Live Webinars",
          description:
            "Interactive webinars with expert coaches to dive deeper into business strategy, explore the ins and outs of entrepreneurship, and get coaching to help you move forward",
          icon: SvgVideoOutline,
        },
        {
          title: "Accountability Support",
          description:
            "Small groups provide a weekly space to connect with peers to share ideas, exchange feedback, and help each other stay on track",
          icon: SvgChat,
        },
        {
          title: "Weekly Challenges",
          description:
            "High-impact, weekly action steps intentionally curated to challenge you in a way that’s manageable, fun, and engineered to drive results in your business",
          icon: SvgListCheck,
        },
        {
          title: "Coach Connections",
          description:
            "Dedicated discussion forums to connect with peers and receive support from IIN coaches and Zoee business experts​",
          icon: SvgUsers,
        },
      ]

  return (
    <div className="py-[56px] md:py-[96px] flex flex-col items-center justify-center overflow-hidden md:px-[144px] px-4 sm:px-8 max-w-screen-xl mx-auto">
      {/* Title Section */}
      <div className="flex flex-col items-start gap-4 w-full max-w-6xl text-left">
        <h2 className="text-black text-5xl md:text-5xl font-bold leading-snug">
          What You’ll Get
        </h2>

        <p
          className="text-black font-normal w-full"
          style={{
            fontSize: "22px",
            lineHeight: "26.4px",
            textAlign: "left",
          }}
        >
          {isBootcamp
            ? "This intensive training gives you the structure, tools, and accountability to build sustainable momentum in your coaching business in just one week."
            : "This action-oriented program provides the practical guidance, tools, and accountability to help you build a thriving coaching practice with minimal overhead so you can focus your time and energy supporting clients and doing what you love. You’ll receive..."}
        </p>
      </div>

      {/* Program Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full max-w-6xl">
        {features.map((feature, index) => (
          <div
            key={index}
            className="flex flex-col items-start gap-4 p-4 rounded-lg"
          >
            {/* Icon */}
            <div className="p-4 bg-[#7190ff] rounded-full flex items-center justify-center">
              <feature.icon className="w-6 h-6 text-white" />
            </div>
            {/* Feature Title */}
            <h3 className="text-black text-[22px] leading-[26.4px] font-bold">
              {feature.title}
            </h3>
            <p className="text-black text-[16px] leading-[24px] font-normal text-left">
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div className="mt-8">
        {isBootcamp ? (
          isCountdownActive ? (
            <button
              className="px-6 py-3 bg-[#4750f5] text-white text-[16px] font-bold rounded-lg flex items-center gap-2"
              onClick={handleBootcampRegistration}
            >
              <span>{"\u{1F449}"}</span> SAVE MY SEAT
            </button>
          ) : (
            <button
              className="px-6 py-3 bg-[#4750f5] text-white text-[16px] font-bold rounded-lg flex items-center gap-2"
              onClick={scrollToJotform}
            >
              <span>{"\u{1F449}"}</span> JOIN WAITLIST
              <SvgArrowRight className="w-5 h-5" />
            </button>
          )
        ) : (
          <button
            className="px-6 py-3 bg-[#4750f5] text-white text-[16px] font-bold rounded-lg flex items-center gap-2"
            onClick={scrollToJotform}
          >
            SAVE MY SEAT
            <SvgArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  )
}

export default ProgramFeatures
